import * as THREE from 'three';

import AbstractObject from '../../../misc/object';
import { HTMLEditableLabel } from '../../../objects/html-label';

import Beam from '../../../objects/beam';
import Panel from '../../../objects/panel';
import Arrow from '../../../objects/arrow';
import Line from '../../../objects/line';

import textureImg from '../../../../assets/textures/steel_2.jpg';
import { unitTransform } from '@services/helper.service';
import { defaultUnits } from '@config';
import Scene from "@scene";
import { LabelsPadding } from '@scene/objects/ground';
import Marker from '../../../objects/marker';

const defaultTexture = new THREE.TextureLoader().load(textureImg);
const labelPadding = 30;

export default class Barrier extends AbstractObject {

  static types = {
    STAIRS: 'stairs',
    WALLS: 'walls',
    profile: {
      FLAT_BARS: 'flat_bars',
      PIPE: 'pipe',
      RECTANGULAR_HOLLOW: 'rectangular_hollow',
      SQUARE_HOLLOW: 'square_hollow',
      ROUND_STEEL_BARS: 'round_steel_bars',
      SQUARE_BARS: 'square_bars'
    }
  };

  constructor(options, texture) {
    super(options);

    this.stays = [];

    this.texture = texture || defaultTexture;

    this.options.barrierPadding = this.options.dpot / 8;

    this.barrier = this.getBarrier();
    this.object.addMesh(this.barrier);
  }

  get size() {
    let { dpot } = this.options;
    if (this.options.type === Barrier.types.WALLS) {
      dpot = this.options.dpot / (this.options.stayCount - 1);

      dpot -= this.options.barrierPadding;
    }

    let width = 0;
    if (this.options.tilt) {
      width = Math.sqrt((dpot ** 2) + (this.options.tilt ** 2)) * (this.options.stayCount - 1);
    } else {
      width = dpot * (this.options.stayCount - 1);
    }

    width += this.options.barrierPadding * 2;

    let depth = this.options.depth;
    let stayHeight = 0;
    if (
      this.options.profileFamily === Barrier.types.profile.SHAPE &&
      this.options.shape &&
      this.options.shape.outer &&
      this.options.shape.outer.length
    ) {
      depth = Math.abs(this.options.shape.outer[0].y - this.options.shape.outer[this.options.shape.outer.length / 2].y);
      stayHeight = Math.abs(this.options.shape.outer[0].x - this.options.shape.outer[this.options.shape.outer.length / 2].x);
    }

    return {
      width,
      dpot,
      height: this.options.height + (this.options.baseOffset || 0),
      depth,
      stayHeight,
      stayWidth: this.options.stayWidth
    };
  }

  get staysPositions() {
    if (this.options.type === Barrier.types.WALLS) {
      return [
        new THREE.Vector3(this.size.width / 2, -this.size.height - this.size.stayWidth / 2, 0),
        new THREE.Vector3(this.size.width / 2, this.size.stayWidth / 2, 0),
        new THREE.Vector3(this.size.width / -2, -this.size.height - this.size.stayWidth / 2, 0),
        new THREE.Vector3(this.size.width / -2, this.size.stayWidth / 2, 0)
      ];
    }

    return this.stays.map(item => (item.mesh.position));
  }

  getBarrier() {
    const object = new THREE.Object3D();
    const tilt = this.options.tilt || 0;

    if (this.options.stayCount > 1) {
      const hafStayCount = Math.ceil(this.options.stayCount / 2);

      for (let i = -hafStayCount + 1; i < hafStayCount; i++) {
        const stay = this.getStay(new THREE.Vector3(
          this.size.dpot * i,
          tilt * i,
          0
        ));

        this.stays.push(stay);
        object.add(stay.mesh);
      }
    } else {
      const stay = this.getStay(new THREE.Vector3(
        0,
        0,
        0
      ));

      this.stays.push(stay);
      object.add(stay.mesh);
    }

    if (!this.options.disableTop) {
      const top = this.getTop();

      this.object.addObject(top);
      object.add(top.mesh);
    }

    if (this.options.type === Barrier.types.WALLS) {
      const bottom = this.getTop();

      bottom.mesh.position.y = -this.size.height / 2 - this.size.stayHeight / 2;

      object.add(bottom.mesh);
    }

    return object;
  }

  /* eslint-disable */
  getStay(position, height = this.size.height) {
    let stay = null;

    switch (this.options.profileFamily) {
      case Barrier.types.profile.FLAT_BARS: {
        stay = new Panel({
          height: height,
          width: this.size.depth,
          depth: this.size.stayWidth
        }, this.texture);

        break;
      }

      case Barrier.types.profile.PIPE: {
        stay = new Beam({
          type: Beam.types.PIPE,
          height: height,
          width: this.size.depth,
          depth: this.size.stayWidth
        });

        break;
      }

      case Barrier.types.profile.RECTANGULAR_HOLLOW:
      case Barrier.types.profile.SQUARE_HOLLOW: {
        stay = new Beam({
          type: Beam.types.SQUARE,
          height,
          width: this.size.depth,
          depth: this.size.stayWidth
        });

        break;
      }

      case Barrier.types.profile.SQUARE_BARS: {
        stay = new Panel({
          height,
          width: this.size.depth,
          depth: this.size.stayWidth
        }, this.texture);

        break;
      }

      case Barrier.types.profile.ROUND_STEEL_BARS: {
        stay = new Panel({
          height,
          width: this.size.depth,
          depth: this.size.stayWidth
        }, this.texture);
        stay.mesh.rotation.y = Math.PI / 4;

        break;
      }

      case Barrier.types.profile.SHAPE: {
        stay = new Beam({
          type: Beam.types.SHAPE,
          shape: this.options.shape,
          height
        });

        break;
      }

      default: {
        throw new Error('Something went wrong!');
      }
    }

    if (position) {
      stay.mesh.position.set(position.x, position.y, position.z);
    }

    return stay;
  }

  getTop() {
    const object = new AbstractObject();

    const top = this.getStay(null, this.size.width);
    object.object.addObject(top);

    const tilt = -(this.size.dpot / this.options.tilt) || Math.PI / 2;

    object.mesh.rotation.z = Math.atan(tilt);
    object.mesh.position.set(0, this.size.height / 2 + this.size.stayHeight / 2, 0);

    return object;
  }

  createHgsLabel(dispatch, options) {
    const lastStayPosition = this.staysPositions[0];
    
    const object = new THREE.Object3D();
    const arrow = new Arrow({
      position: {
        x: this.position.x,
        y: Math.abs(lastStayPosition.y) + this.position.y - this.options.height / 2 + this.options.baseOffset / 2,
        z: this.position.z + lastStayPosition.x - LabelsPadding
      },
      size: this.size.height - this.options.baseOffset,
      direction: Arrow.types.direction.Y,
      disableDepthTest: true
    });
    
    object.add(arrow.mesh);
    
    const topLine = new Line({
      from: {
        x: arrow.position.x,
        y: arrow.position.y,
        z: arrow.position.z
      },
      to: {
        x: arrow.position.x,
        y: arrow.position.y,
        z: arrow.position.z + LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(topLine.mesh);

    const bottomLine = new Line({
      from: {
        x: arrow.position.x,
        y: arrow.position.y + this.options.height,
        z: arrow.position.z
      },
      to: {
        x: arrow.position.x,
        y: arrow.position.y + this.options.height,
        z: arrow.position.z + LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(bottomLine.mesh);

    const htmlElement = new HTMLEditableLabel({
      text: (options.showLabels ? 'Hgc: ' : '') + 
        `${unitTransform(+this.options.height).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.height).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      align: HTMLEditableLabel.types.align.LEFT,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      dispatch({
        barrier: {
          height: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });
    // htmlElement.setDirection(arrow.position, arrow.endPosition, true);
    htmlElement.position.set(
      this.position.x,
      Math.abs(lastStayPosition.y) + this.position.y + this.options.baseOffset / 2,
      lastStayPosition.x - this.size.depth / 2
    );

    return {
      arrow: object,
      htmlElement
    };
  }

  createDpotLabel(dispatch, options) {
    const dpot = {
      arrow: new Arrow({
        position: this.position,
        size: -this.options.dpot, // Minus for arrow inverting
        direction: Arrow.types.direction.Z,
        disableDepthTest: true
      }),
      htmlElement: new HTMLEditableLabel({
        text: (options.showLabels ? 'Dpot: ' : '') + 
          `${unitTransform(+this.options.dpot).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
        value: unitTransform(+this.options.dpot).from(defaultUnits.DISTANCE).to(Scene.units.distance),
        align: HTMLEditableLabel.types.align.CENTER,
        minValue: 0
      })
    };

    if (!options.displayHorizontally)
      dpot.htmlElement.setDirection(dpot.arrow.position, dpot.arrow.endPosition);
    dpot.htmlElement.onInputChange((value, didChange) => {
      dispatch({
        barrier: {
          dpot: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });
    dpot.htmlElement.position.set(
      this.position.x,
      this.position.y,
      this.position.z - this.options.dpot / 2
    );

    return dpot;
  }

  createShiftLabels(dispatch, shift, panelCenter, options) {
    var marker = null;
    var xLabel = null;
    var yLabel = null;
    if (Number(shift.x) !== 0 || Number(shift.y) !== 0) {
      marker = new Marker({ size: 8 });
      marker.position.set(panelCenter.x, panelCenter.y, panelCenter.z);

      if (Number(shift.x) !== 0) {
        xLabel = this.getXLabel(shift, panelCenter, dispatch, options);
      }

      if (Number(shift.y) !== 0) {
        yLabel = this.getYLabel(shift, panelCenter, dispatch, options);
      }
    }
    return {
      marker : marker,
      xLabel : xLabel,
      yLabel : yLabel
    }
  }

  getXLabel(shift = {}, relativePos, dispatch, options) {
    shift.x = shift.x || 0;
    shift.y = shift.y || 0;

    const object = new THREE.Object3D();
    const padding = shift.y >= 0 ? labelPadding : -labelPadding;
    const offset = 3;

    object.add(new Line({
      disableDepthTest: true,
      from: { x: relativePos.x + shift.x, y: relativePos.y - padding + (shift.y >= 0 ? -offset : offset), z: relativePos.z },
      to: { x: relativePos.x + shift.x, y: (-relativePos.y - shift.y + (shift.y >= 0 ? offset : -offset)) * (-1), z: relativePos.z }
    }).mesh);

    object.add(new Line({
      disableDepthTest: true,
      from: { x: relativePos.x, y: relativePos.y - padding + (shift.y >= 0 ? -offset : offset), z: relativePos.z },
      to: { x: relativePos.x, y: relativePos.y, z: relativePos.z }
    }).mesh);

    object.add(new Arrow({
      disableDepthTest: true,
      position: new THREE.Vector3(
        relativePos.x, relativePos.y - padding, relativePos.z
      ),
      size: shift.x,
      direction: Arrow.types.direction.X,
      arrowSize: 15
    }).mesh);

    const label = new HTMLEditableLabel({
      text: (!options || options.showLabels ? 'Ex = ' : '') + 
        `${unitTransform(+shift.x).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+shift.x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: shift.y >= 0 ? HTMLEditableLabel.types.verticalAlign.TOP : HTMLEditableLabel.types.verticalAlign.BOTTOM
    });
    label.position.set(
      relativePos.x + shift.x / 2, relativePos.z, -relativePos.y + padding
    );
    label.onInputChange((value, didChange) => {
      dispatch({
        beam: {
          shift: {
            x: unitTransform(+value).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });

    return {
      object,
      HTMLObject: label
    }
  }

  getYLabel(shift = {}, relativePos, dispatch, options) {
    shift.x = shift.x || 0;
    shift.y = shift.y || 0;

    const object = new THREE.Object3D();
    const padding = shift.x >= 0 ? labelPadding : -labelPadding;
    const offset = 3;

    object.add(new Line({
      disableDepthTest: true,
      from: { x: relativePos.x - padding + (shift.x >= 0 ? -offset : offset), y: relativePos.y + shift.y, z: relativePos.z },
      to: { x: (-relativePos.x - shift.x + (shift.x >= 0 ? offset : -offset)) * (-1), y: relativePos.y + shift.y, z: relativePos.z }
    }).mesh);

    object.add(new Line({
      disableDepthTest: true,
      from: { x: relativePos.x - padding + (shift.x >= 0 ? -offset : +offset), y: relativePos.y, z: relativePos.z },
      to: { x: relativePos.x, y: relativePos.y, z: relativePos.z }
    }).mesh);

    object.add(new Arrow({
      disableDepthTest: true,
      position: new THREE.Vector3(
        relativePos.x - padding, relativePos.y, relativePos.z
      ),
      size: shift.y,
      direction: Arrow.types.direction.Y,
      arrowSize: 15
    }).mesh);

    this.label = new HTMLEditableLabel({
      text: (!options || options.showLabels ? 'Ey = ' : '') + 
        `${unitTransform(+shift.y).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+shift.y).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: shift.x >= 0 ? HTMLEditableLabel.types.align.RIGHT : HTMLEditableLabel.types.align.LEFT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER
    });
    this.label.position.set(
      // -labelPadding - shift.x, 0, shift.y / 2
      relativePos.x -padding, relativePos.z, -relativePos.y - shift.y / 2
    );
    this.label.onInputChange((value, didChange) => {
      dispatch({
        beam: {
          shift: {
            y: unitTransform(+value).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });

    return {
      object,
      HTMLObject: this.label
    }
  }
}



// WEBPACK FOOTER //
// ./src/object-view/generators/safety-barrier/objects/barrier.js